"use client";

import { useState } from "react";
import type { MessengerBot } from "@/application/notifications";
import type { MessengerChat } from "@/domain/ports";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Button, ErrorBox } from "../ui";
import type { TableRow } from "./SettingsTable";
import { useRequest } from "./useRequest";
import { Modal, useDialog } from "@/components/Dialog";

/**
 * The platform table's row of the Telegram bot (administrators): the bot and the chat it posts the new trade tips to, or
 * what is missing, as a dot and its text — and "set up" / "manage" where the defaults stand, which opens its own window
 * (`TelegramSetup`). Saved by itself, no default.
 */
export function useMessengerRow(): TableRow {
  const { t } = useI18n();
  const { data, reload } = useApi<{ bot: MessengerBot }>("/api/platform/messenger");
  const [setup, setSetup] = useState(false);
  const base = { key: "messenger", label: t("set.messenger.row"), description: t("set.messenger.hint") };
  if (!data) return { ...base, input: <div className="skeleton h-6 w-full" /> };
  const { bot } = data;
  const state = stateOf(t, bot);
  const close = () => {
    setSetup(false);
    reload();
  };
  const input = (
    <span className="flex min-w-0 items-center gap-2">
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${state.dot}`} />
      <span className="min-w-0 truncate" title={state.text}>{state.text}</span>
      {/* the window lives with the status (rendered once; the button stands twice: in the default column, on phones here) */}
      {setup && <TelegramSetup bot={bot} onClose={close} />}
    </span>
  );
  const action = <Button type="button" size="xs" onClick={() => setSetup(true)}>{t(bot.bot ? "set.messenger.change" : "set.messenger.setUp")}</Button>;
  return { ...base, input, action };
}

const LINK = "shrink-0 cursor-pointer text-[11.5px] font-medium hover:underline disabled:opacity-50";

/** What the row says, with its dot: the bot and its chat (green), the bot without a chat yet (amber), nothing set up (grey). */
function stateOf(t: ReturnType<typeof useI18n>["t"], bot: MessengerBot): { text: string; dot: string } {
  if (bot.bot && bot.chat) return { text: t("set.messenger.posting", { bot: bot.bot, chat: bot.chat.title }), dot: "bg-good" };
  if (bot.bot) return { text: t("set.messenger.noChat", { bot: bot.bot }), dot: "bg-warn" };
  return { text: t("set.messenger.none"), dot: "bg-line-strong" };
}

/** The window: 1. the bot (its token), 2. the chat it posts to — the second once a bot is stored; removing the bot at its foot. */
function TelegramSetup({ bot, onClose }: { bot: MessengerBot; onClose: () => void }) {
  const { t } = useI18n();
  const dialog = useDialog();
  const [current, setCurrent] = useState(bot);
  const { busy, error, run } = useRequest();
  const remove = async () => {
    if (!(await dialog.confirm(t("set.messenger.removeConfirm", { bot: current.bot ?? "" }), { danger: true }))) return;
    await run(async () => {
      await api("/api/platform/messenger", { method: "DELETE" });
      onClose();
    });
  };
  return (
    <Modal label={t("set.messenger.setUpTitle")} onClose={onClose} size="wide">
      <div className="space-y-4 text-[13px]">
        <h2 className="text-[15px] font-semibold">{t("set.messenger.setUpTitle")}</h2>
        <BotStep bot={current} onSaved={setCurrent} />
        {current.bot && <ChatStep bot={current} onChosen={setCurrent} />}
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="flex items-center gap-2 border-t border-line pt-3">
          {current.bot && <button type="button" disabled={busy} onClick={() => void remove()} className={`${LINK} text-muted hover:text-bad-ink`}>{t("set.messenger.remove")}</button>}
          <Button type="button" size="sm" onClick={onClose} className="ml-auto">{t("action.close")}</Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Step 1, the bot: stored — its name, and another one can replace it; else the steps in Telegram (@BotFather, /newbot)
 * and a large field for the token — its bare value or the whole reply it came in (the server finds the token in it).
 */
function BotStep({ bot, onSaved }: { bot: MessengerBot; onSaved: (bot: MessengerBot) => void }) {
  const { t } = useI18n();
  const [replace, setReplace] = useState(!bot.bot);
  const [text, setText] = useState("");
  const { busy, error, run } = useRequest();
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      onSaved((await api<{ bot: MessengerBot }>("/api/platform/messenger", { method: "PUT", json: { token: text } })).bot);
      setReplace(false);
      setText("");
    });
  };
  if (!replace)
    return (
      <section className="flex items-center gap-2">
        <span className="font-medium">{t("set.messenger.step.bot")}</span>
        <span className="min-w-0 flex-1 truncate text-good-ink">✓ {bot.bot}</span>
        <button type="button" onClick={() => setReplace(true)} className={`${LINK} text-accent-ink`}>{t("set.messenger.otherBot")}</button>
      </section>
    );
  return (
    <form onSubmit={save} className="space-y-2">
      <h3 className="font-medium">{t("set.messenger.step.bot")}</h3>
      <ol className="list-decimal space-y-1 pl-5 text-ink-2">
        <li>{t("set.messenger.step1")}</li>
        <li>{t("set.messenger.step2")}</li>
        <li>{t("set.messenger.step3")}</li>
      </ol>
      <label className="block space-y-1">
        <span className="text-[12px] font-medium">{t("set.messenger.token")}</span>
        <textarea autoFocus required rows={3} spellCheck={false} autoComplete="off" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("set.messenger.tokenHelp")} className="w-full resize-none rounded-md border border-line bg-surface-2 px-2.5 py-2 font-mono text-[12.5px] break-all" />
      </label>
      <p className="text-[11.5px] text-muted">{t("set.messenger.saveHint")}</p>
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="flex justify-end gap-2">
        {bot.bot && <Button type="button" size="sm" onClick={() => setReplace(false)}>{t("action.cancel")}</Button>}
        <Button type="submit" size="sm" variant="primary" disabled={busy || !text.trim()}>{busy ? t("common.working") : t("set.messenger.save")}</Button>
      </div>
    </form>
  );
}

/**
 * Step 2, the chat the bot posts the new tips to: add the bot to a channel or group (as an administrator), then look
 * for it; the channels and groups the bot is in are listed — choosing one, the bot says so there.
 */
function ChatStep({ bot, onChosen }: { bot: MessengerBot; onChosen: (bot: MessengerBot) => void }) {
  const { t } = useI18n();
  const [chats, setChats] = useState<MessengerChat[] | null>(null);
  const { busy, error, run } = useRequest();
  const search = () => run(async () => setChats((await api<{ chats: MessengerChat[] }>("/api/platform/messenger/chat")).chats));
  const choose = (c: MessengerChat) => run(async () => onChosen((await api<{ bot: MessengerBot }>("/api/platform/messenger/chat", { method: "PUT", json: { chatId: c.id } })).bot));
  return (
    <section className="space-y-2 border-t border-line pt-3">
      <h3 className="font-medium">{t("set.messenger.step.chat")}</h3>
      {bot.chat && <p className="text-good-ink">✓ {t("set.messenger.chatNow", { chat: bot.chat.title })}</p>}
      <ol className="list-decimal space-y-1 pl-5 text-ink-2">
        <li>{t("set.messenger.chatStep1", { bot: bot.bot ?? "" })}</li>
        <li>{t("set.messenger.chatStep2")}</li>
      </ol>
      <Button type="button" size="sm" disabled={busy} onClick={() => void search()}>{busy ? t("common.working") : t("set.messenger.search")}</Button>
      {chats?.length === 0 && <p className="text-[12px] text-muted">{t("set.messenger.noChats", { bot: bot.bot ?? "" })}</p>}
      {chats && chats.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line">
          {chats.map((c) => <ChatItem key={c.id} chat={c} chosen={bot.chat?.id === c.id} busy={busy} onChoose={() => void choose(c)} />)}
        </ul>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </section>
  );
}

/** One chat the bot is in: its title and kind, and "choose" (or that it is the chosen one). */
function ChatItem({ chat, chosen, busy, onChoose }: { chat: MessengerChat; chosen: boolean; busy: boolean; onChoose: () => void }) {
  const { t } = useI18n();
  return (
    <li className="flex items-center gap-2 px-2.5 py-1.5">
      <span className="min-w-0 flex-1 truncate">{chat.title}</span>
      <span className="shrink-0 text-[11.5px] text-muted">{t(`set.messenger.kind.${chat.kind}`)}</span>
      {chosen ? <span className="shrink-0 text-[11.5px] font-medium text-good-ink">{t("set.messenger.chosen")}</span> : <Button type="button" size="sm" disabled={busy} onClick={onChoose}>{t("set.messenger.choose")}</Button>}
    </li>
  );
}
